import { RECORDS_PER_PAGE, TOAST_VARIANT } from "@root/constants";
import { SubscriberService } from "@root/services";
import { useState } from "react";
import {
  FETCH_SUBSCRIBERS,
  DELETE_SUBSCRIBER,
  CREATE_SUBSCRIBER,
  UPDATE_SUBSCRIBER,
  IMPORT_SUBSCRIBERS,
  FETCH_SUBSCRIBER_MEMBERS,
  FETCH_SUBSCRIBER_COURSES,
  FETCH_SUBSCRIBER_COURSE_ENROLLEDMEMBERS,
  FETCH_SUBSCRIBER_COURSE_JOURNALIST,
} from "@root/store/action-types/subscriber";
import { useNotification } from "./useNotification";
import { useTranslation } from "react-i18next";
import { useOrganizationBranding } from "@root/context/organization-branding-context";

export function useSubscriber() {
  const { slug } = useOrganizationBranding();
  const subscriberService = new SubscriberService();
  const { showNotification } = useNotification();
  const [subscribers, setSubscribers] = useState([]);
  const [subscriberMembers, setSubscriberMembers] = useState<string[]>([]);
  const [subscriberCourses, setSubscriberCourses] = useState<string[]>([]);
  const [enrolledmembers, setEnrolledmembers] = useState<string[]>([]);
  const [subscriberJournalist, setSubscriberJournalist] = useState<string[]>(
    []
  );
  const [fetchSubscriberCoursesLoading, setFetchSubscriberCoursesLoading] =
    useState(false);
  const [
    fetchSubscriberJournalistLoading,
    setFetchSubscriberJournalistLoading,
  ] = useState(false);
  const [fetchSubscribersLoading, setFetchSubscribersLoading] = useState(false);
  const [enrolledMembersLoading, setEnrolledMembersLoading] = useState(false);
  const [fetchSubscribersMembersLoading, setFetchSubscribersMembersLoading] =
    useState(false);
  const [createSubscribersLoading, setCreateSubscribersLoading] =
    useState(false);
  const [deleteSubscribersLoading, setDeleteSubscribersLoading] =
    useState(false);
  const [updateSubscribersLoading, setUpdateSubscribersLoading] =
    useState(false);
  const [importSubscribersLoading, setImportSubscribersLoading] =
    useState(false);
  const [loader, setLoader] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const { t } = useTranslation("common");

  const pageSize = RECORDS_PER_PAGE;

  const payload = {
    offset: (pageNo - 1) * pageSize,
    limit: pageSize,
  };

  const onSuccess = (data: any, identifier: any) => {
    if (identifier === FETCH_SUBSCRIBERS) {
      setFetchSubscribersLoading(false);
      setSubscribers(data?.[0] || []);
      setPageCount(data?.[1]);
    }
    if (identifier === CREATE_SUBSCRIBER) {
      setCreateSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("subscriberManagement.createSuccess"),
      });
      //fetchSubscribers(payload);
    }
    if (identifier === DELETE_SUBSCRIBER) {
      setDeleteSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("subscriberManagement.deleteSuccess"),
      });
    }
    if (identifier === UPDATE_SUBSCRIBER) {
      setUpdateSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t("subscriberManagement.updateSuccess"),
      });
    }
    if (identifier === IMPORT_SUBSCRIBERS) {
      setImportSubscribersLoading(false);
      const imported = data?.imported ?? 0;
      showNotification({
        type: TOAST_VARIANT.SUCCESS,
        message: t(
          "subscriberManagement.importSuccess",
          `Successfully imported ${imported} subscriber(s)`,
        ),
      });
    }
    if (identifier === FETCH_SUBSCRIBER_MEMBERS) {
      setFetchSubscribersMembersLoading(false);
      setSubscriberMembers(data?.rows || []);
      setPageCount(data?.count);
    }
    if (identifier === FETCH_SUBSCRIBER_COURSES) {
      setFetchSubscriberCoursesLoading(false);
      setSubscriberCourses(data?.rows || []);
      setPageCount(data?.count);
    }

    if (identifier === FETCH_SUBSCRIBER_COURSE_ENROLLEDMEMBERS) {
      setEnrolledMembersLoading(false);
      setEnrolledmembers(data || []);
      setPageCount(data?.count);
    }

    if (identifier === FETCH_SUBSCRIBER_COURSE_JOURNALIST) {
      setFetchSubscriberJournalistLoading(false);
      setSubscriberJournalist(data || []);
      setPageCount(data?.count);
    }
  };

  const onError = (error: any, identifier: any) => {
    const fallbackMessage = t("inquiriesTab.genericError");
    const errorMessage =
      error?.response?.data?.message || error?.message || fallbackMessage;

    if (identifier === FETCH_SUBSCRIBERS) {
      setFetchSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === CREATE_SUBSCRIBER) {
      setCreateSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === DELETE_SUBSCRIBER) {
      setDeleteSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === UPDATE_SUBSCRIBER) {
      setUpdateSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === IMPORT_SUBSCRIBERS) {
      setImportSubscribersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === FETCH_SUBSCRIBER_MEMBERS) {
      setFetchSubscribersMembersLoading(false);
      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
    if (identifier === FETCH_SUBSCRIBER_COURSES) {
      setFetchSubscriberCoursesLoading(false);

      showNotification({
        type: TOAST_VARIANT.ERROR,
        message: errorMessage,
      });
    }
  };

  const fetchSubscribers = async (payload: any) => {
    setFetchSubscribersLoading(true);
    const identifier = FETCH_SUBSCRIBERS;
    try {
      const response = await subscriberService.fetchSubscribers({
        ...(slug && { slug }),
        ...payload,
      });
      onSuccess(response?.data.data, identifier);
    } catch (error: unknown) {
      onError(error, identifier);
    }
  };

  const createSubscriber = async (data: any) => {
    setCreateSubscribersLoading(true);
    const identifier = CREATE_SUBSCRIBER;
    try {
      const response = await subscriberService.createSubscriber(data);
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const updateSubscriber = async (data: any) => {
    setUpdateSubscribersLoading(true);
    const identifier = UPDATE_SUBSCRIBER;
    try {
      const response = await subscriberService.updateSubscriber(data);
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const importSubscribers = async (file: File) => {
    setImportSubscribersLoading(true);
    const identifier = IMPORT_SUBSCRIBERS;
    try {
      const response = await subscriberService.importSubscribers(file);
      onSuccess(response?.data?.data ?? response?.data, identifier);
      return response?.data?.data ?? response?.data;
    } catch (error) {
      onError(error, identifier);
      throw error;
    }
  };

  const updateSubscriberById = async (id: number, data: any) => {
    setUpdateSubscribersLoading(true);
    const identifier = UPDATE_SUBSCRIBER;
    try {
      const response = await subscriberService.updateSubscriberById(id, data);
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
      throw error;
    }
  };

  const deleteSubscriber = async (data: any) => {
    setDeleteSubscribersLoading(true);
    const identifier = DELETE_SUBSCRIBER;
    try {
      const response = await subscriberService.deleteSubscriber(data);
      onSuccess(response?.data?.data, identifier);
      fetchSubscribers(payload);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const fetchSubscriberMembers = async (data: any) => {
    setFetchSubscribersMembersLoading(true);
    const identifier = FETCH_SUBSCRIBER_MEMBERS;
    try {
      const response = await subscriberService.fetchSubscriberMembers(data);
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const fetchSubscriberCourses = async (id: number, data: any) => {
    setFetchSubscriberCoursesLoading(true);
    const identifier = FETCH_SUBSCRIBER_COURSES;
    try {
      const response = await subscriberService.fetchSubscriberCourses(id, data);
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const fetchEnrolledUsers = async (
    //Admin portal
    courseId: number,
    subscriberId: number,
    payload: any
  ) => {
    setFetchSubscriberCoursesLoading(true);
    const identifier = FETCH_SUBSCRIBER_COURSE_ENROLLEDMEMBERS;
    try {
      const response = await subscriberService.enrolledUsers(
        courseId,
        subscriberId,
        payload
      );
      onSuccess(response?.data?.data, identifier);
    } catch (error) {
      onError(error, identifier);
    }
  };

  const fetchSubscriberJournalists = async (
    //pledging portal
    courseId: number,
    payload: any
  ) => {
    setFetchSubscriberJournalistLoading(true);
    const identifier = FETCH_SUBSCRIBER_COURSE_JOURNALIST;
    try {
      const response = await subscriberService.subscriberJournalist(
        courseId,
        payload
      );
      console.log(response);
      onSuccess(response?.data?.data, identifier);
      return response?.data?.data;
    } catch (error) {
      onError(error, identifier);
    }
  };

  return {
    fetchSubscribers,
    fetchEnrolledUsers,
    subscriberCourses,
    enrolledmembers,
    fetchSubscriberMembers,
    fetchSubscriberCourses,
    fetchSubscriberJournalists,
    subscriberJournalist,
    pageNo,
    pageSize,
    setPageNo,
    pageCount,
    subscribers,
    setSubscribers,
    subscriberMembers,
    setSubscriberMembers,
    createSubscriber,
    updateSubscriber,
    updateSubscriberById,
    importSubscribers,
    importSubscribersLoading,
    deleteSubscriber,
    loader,
    setLoader,
    createSubscribersLoading,
    updateSubscribersLoading,
    fetchSubscriberCoursesLoading,
  };
}
export default useSubscriber;
